import React, { useEffect, useContext, useRef } from 'react'
import Layout from '../../components/layout/layout'
import './home.scss'
import FAQ from '../../components/faq/faq'
import EngagementModels from '../../components/EngagementModels/EngagementModels'
import { useNavigate } from 'react-router-dom'
import Link from '../../components/LanguageSwitcher/LocaleLink'
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import articles from '../../data/articles'
import { articleCovers, localizeArticle } from '../articles/articlePage/articleShared'
import allServices, { SERVICE_CATEGORIES } from '../../data/services'
import { localizeServices } from '../../data/localizeService'
import Reveal from '../../components/Reveal/Reveal'
import HeroVideo from '../../components/HeroVideo/HeroVideo'
import Seo, {
  organizationJsonLd,
  websiteJsonLd,
  faqJsonLd,
  serviceJsonLd,
  breadcrumbJsonLd,
} from '../../components/Seo/Seo'
import QuoteForm from '../../components/QuoteForm/QuoteForm'
import ServiceSpotlight from '../../components/ServiceSpotlight/ServiceSpotlight'
import AbstractVisual from '../../components/AbstractVisual/AbstractVisual'
import WorkShowcase from '../../components/WorkShowcase/WorkShowcase'
import ScrambleValue from '../../components/motion/ScrambleValue'
import MagneticButton from '../../components/motion/MagneticButton'
import VelocityMarquee from '../../components/motion/VelocityMarquee'
import SplitHeading from '../../components/motion/SplitHeading'
import useEntryMotion from '../../components/motion/useEntryMotion'
// Stock scenes (Unsplash) shared with /process; not our team, see src/images/process/README.md
import frameImg from '../../images/process/frame.jpg'
import dataImg from '../../images/process/data.jpg'
import prototypeImg from '../../images/process/prototype.jpg'
import productionImg from '../../images/process/production.jpg'
import projects, { resolveProjects } from '../../data/projects'
import ogImage from '../../images/archi/medicProductPage.jpg'
import { faqQuestions } from '../../components/faq/faq'
import pythonLogo from '../../images/techs/color/python.svg'
import pytorchLogo from '../../images/techs/color/pytorch.svg'
import tensorflowLogo from '../../images/techs/color/tensorflow.svg'
import scikitLogo from '../../images/techs/color/scikit-learn.svg'
import pandasLogo from '../../images/techs/color/pandas.svg'
import jupyterLogo from '../../images/techs/color/jupyter.svg'
import reactLogo from '../../images/techs/color/react.svg'
import nodeLogo from '../../images/techs/color/nodejs.svg'
import postgresLogo from '../../images/techs/color/postgresql.svg'
import dockerLogo from '../../images/techs/color/docker.svg'
import awsLogo from '../../images/techs/color/aws.svg'
import gcloudLogo from '../../images/techs/color/googlecloud.svg'


const Home = () => {

  useEffect(() => {
    window.scrollTo(0, 0); 
  }, []);

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
  };

  const lineVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06 } },
  };

  const wordVariants = {
    hidden: { y: '110%' },
    visible: { y: '0%', transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  };

  // `as` lets the hero headline render as an <h1> while the rest stay <p>.
  const AnimatedLine = ({ text, className, as: Tag = 'p' }) => {
    const Motion = motion[Tag];
    return (
      <Motion variants={animateIn ? lineVariants : undefined} className={className}>
      {String(text || '').split(' ').map((word, i) => (
        <span key={i} className='word-mask'>
          <motion.span variants={animateIn ? wordVariants : undefined} className='word'>
            {word}{' '}
          </motion.span>
        </span>
      ))}
      </Motion>
    );
  };

  const marqueeItems = [
    'Machine Learning', 'Computer Vision', 'NLP', 'Data Science', 'AI Consulting', 'Deep Learning',
  ];

  const navigate = useNavigate();

  const { translations } = useContext(LanguageContext);

  const servicesSectionRef = useRef(null);
  const heroRef = useRef(null);
  const reducedMotion = useReducedMotion();
  // False in a background tab, where the paused frame loop would otherwise
  // leave the whole hero stuck at opacity 0.
  const animateIn = useEntryMotion();

  // Hero parallax: the copy and the form drift apart and fade as the page
  // scrolls away, so the fold has depth instead of sliding as one block.
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const heroCopyY = useTransform(heroProgress, [0, 1], [0, reducedMotion ? 0 : -90]);
  const heroFormY = useTransform(heroProgress, [0, 1], [0, reducedMotion ? 0 : 60]);
  const heroFade = useTransform(heroProgress, [0, 0.75], [1, reducedMotion ? 1 : 0.15]);

const handleScrollToAboutSection = () => {
  if (servicesSectionRef.current) {
    servicesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};


  // The real catalogue (data/services.js), grouped like the /services hub.
  const catalogue = localizeServices(allServices, translations)
  const servicesBox = catalogue.map((service) => ({ title: service.name, description: service.summary }))
  const categoryLabel = (id) => translations[`svcCat${id.charAt(0).toUpperCase()}${id.slice(1)}`]

  // The three newest articles from the shared template.
  const blogBox = articles.slice(0, 3).map((article) => {
    const local = localizeArticle(article, translations)
    return { img: articleCovers[article.cover], title: local.title, desc: local.description, link: `/articles/${article.slug}` }
  })

  const handleNavigateToArticle = (link) => {
    navigate(link)
  }

  const handleNavigateAboutUs = () => {
    navigate('/about-us')
  }

  const handleNavigateProjects = () => {
    navigate('/projects')
  }

  // Every "start a project" call to action leads to the contact form.
  // Every "start a project" call to action leads back to the hero form.
  const handleStartBrief = () => {
    const card = document.getElementById('get-estimate')
    if (!card) {
      navigate('/contact-us')
      return
    }
    card.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const firstField = document.getElementById('quote-name')
    if (firstField) setTimeout(() => firstField.focus({ preventScroll: true }), 600)
  }

  const stats = [
    { value: String(projects.length), label: translations.homeStat1 },
    { value: '3–6', label: translations.homeStat2 },
    { value: '3–4', label: translations.homeStat3 },
    { value: '24h', label: translations.homeStat4 },
  ]

  const processSteps = [
    { step: '01', photo: frameImg, title: translations.homeProcess1, text: translations.homeProcess2 },
    { step: '02', photo: dataImg, title: translations.homeProcess3, text: translations.homeProcess4 },
    { step: '03', photo: prototypeImg, title: translations.homeProcess5, text: translations.homeProcess6 },
    { step: '04', photo: productionImg, title: translations.homeProcess7, text: translations.homeProcess8 },
  ]

  const featuredWork = resolveProjects(translations, { featuredOnly: true })

  const handleOpenWork = (link) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener')
      return
    }
    navigate(link)
  }

  const techLogos = [
    { src: pythonLogo, name: 'Python' },
    { src: pytorchLogo, name: 'PyTorch' },
    { src: tensorflowLogo, name: 'TensorFlow' },
    { src: scikitLogo, name: 'scikit-learn' },
    { src: pandasLogo, name: 'Pandas' },
    { src: jupyterLogo, name: 'Jupyter' },
    { src: reactLogo, name: 'React' },
    { src: nodeLogo, name: 'Node.js' },
    { src: postgresLogo, name: 'PostgreSQL' },
    { src: dockerLogo, name: 'Docker' },
    { src: awsLogo, name: 'AWS' },
    { src: gcloudLogo, name: 'Google Cloud' },
  ]

  // The lead magnets that have no teaser of their own above.
  const freeTools = [
    { to: '/seo-check', kicker: translations.seocKicker, title: translations.seocTitle },
    { to: '/accessibility-check', kicker: translations.a11yKicker, title: translations.a11yTitle },
    { to: '/ai-savings', kicker: translations.aisKicker, title: translations.aisTitle },
  ]

  const industries = [
    translations.homeIndustry1,
    translations.homeIndustry2,
    translations.homeIndustry3,
    translations.homeIndustry4,
    translations.homeIndustry5,
    translations.homeIndustry6,
  ]

  return (
    <>
      
      <Layout>
        <Seo
          title='Machine Learning & AI Solutions for Business'
          description='MLMind is a machine learning team building AI products for healthcare, architecture, and e-commerce — medical image analysis, BIM automation, chatbots, and custom ML solutions.'
          image={ogImage}
          jsonLd={[
            organizationJsonLd(),
            websiteJsonLd(),
            breadcrumbJsonLd([{ name: 'Home', path: '/' }]),
            serviceJsonLd(servicesBox),
            faqJsonLd(faqQuestions(translations)),
          ]}
        />
        <div className='header-container'>

          <div className='home-header'>
            <div className='hero-stage'>
            <HeroVideo />
            <div className='header-head' ref={heroRef}>
              <motion.div
                className="header-head-left"
                initial={animateIn ? 'hidden' : false}
                animate="visible"
                variants={containerVariants}
                style={{ y: heroCopyY, opacity: heroFade }}
              >
                <motion.p variants={itemVariants} className='header-head-title'>MLMind</motion.p>
                <AnimatedLine as='h1' text={translations.heroLine1} className='header-head-title-bold no-margin' />
                <AnimatedLine text={translations.heroLine2} className='header-head-title-bold no-margin-top home-hero-accent' />

                <motion.p variants={itemVariants} className='header-head-txtes-block home-hero-sub'>
                  {translations.heroSub}
                </motion.p>

                <motion.div variants={itemVariants} className='home-head-btn-block'>
                  <MagneticButton onClick={handleStartBrief} className='home-services-btn'>{translations.homeStartProject}</MagneticButton>
                  <button onClick={handleNavigateProjects} className='home-services-btn home-recent-work'>{translations.recentProjects}</button>
                  <button onClick={handleScrollToAboutSection} className='home-services-btn home-recent-work home-ghost-btn'>{translations.ourServices}</button>
                </motion.div>

                <motion.ul variants={itemVariants} className='home-head-usps'>
                  <li>{translations.homeUsp1}</li>
                  <li>{translations.homeUsp2}</li>
                  <li>{translations.homeUsp3}</li>
                </motion.ul>
              </motion.div>

              <motion.div
                className='hero-quote'
                initial={animateIn ? { opacity: 0, y: 30 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                style={{ y: heroFormY }}
              >
                <QuoteForm />
              </motion.div>
            </div>
            </div>

            <VelocityMarquee items={marqueeItems} />

            <div className='home-stats'>
              {stats.map((stat, index) => (
                <Reveal key={stat.label} delay={index * 0.08} className='home-stat'>
                  <p className='home-stat-value'>
                    <ScrambleValue value={stat.value} />
                  </p>
                  <p className='home-stat-label'>{stat.label}</p>
                </Reveal>
              ))}
            </div>

            {/* Proof before pitch: the shipped work is the first thing after the fold. */}
            <WorkShowcase
              projects={featuredWork}
              onOpen={handleOpenWork}
              kicker={translations.workKicker}
              title={translations.workTitle}
              intro={translations.workIntro}
              allLabel={translations.workAll}
              onAll={handleNavigateProjects}
              prevLabel={translations.workPrev}
              nextLabel={translations.workNext}
            />

            <div className="scroll-container">
              <div className="scroll-content">
                <Reveal>
                  <p className='home-services-txt'>{translations.whyChoose}</p>
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className='home-why-title'>{translations.whyTitle}</h2>
                </Reveal>
                <ol className='home-why-points'>
                  {[1, 2, 3].map((n, i) => (
                    <Reveal key={n} delay={0.15 + i * 0.08} className='home-why-point'>
                      <span className='home-why-num'>0{n}</span>
                      <div>
                        <h3>{translations[`why${n}t`]}</h3>
                        <p>{translations[`why${n}d`]}</p>
                      </div>
                    </Reveal>
                  ))}
                </ol>
                <Reveal delay={0.3}>
                  <button onClick={handleNavigateAboutUs} className='home-services-btn home-recent-work home-know-more-btn'>{translations.knowMore}</button>
                </Reveal>
              </div>
              <AbstractVisual className='home-why-visual' shape='weave' colors={['#1e1b4b', '#f472b6', '#38bdf8']} />
            </div>
            <Reveal>
              <p className='home-services-txt'>{translations.ourServices}</p>
              <SplitHeading text={translations.homeSvcTitle} className='home-released-projects home-svc-title' />
            </Reveal>
            <div ref={servicesSectionRef} className='home-svc-grid'>
              {SERVICE_CATEGORIES.map((category, index) => (
                <Reveal key={category.id} delay={index * 0.08} className='home-svc-col'>
                  <p className='home-svc-cat'><span>0{index + 1}</span>{categoryLabel(category.id) || category.label}</p>
                  <ul>
                    {catalogue.filter((service) => service.category === category.id).map((service) => (
                      <li key={service.slug}>
                        <Link to={`/services/${service.slug}`} className='home-svc-link'>
                          <span className='home-svc-name'>{service.name}</span>
                          <span className='home-svc-arrow' aria-hidden='true'>→</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <Link to='/services' className='home-all-services'>
                {translations.homeAllServices} <span aria-hidden='true'>→</span>
              </Link>
            </Reveal>

            <ServiceSpotlight set='home' className='home-spot' />



            <Reveal>
              <div className='home-tech'>
                <p className='home-tech-label'>{translations.techLabel}</p>
                <div className='home-tech-logos'>
                  {techLogos.map((tech) => (
                    <div key={tech.name} className='home-tech-logo' title={tech.name}>
                      <img src={tech.src} alt={tech.name} loading='lazy' />
                      <span>{tech.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal>
              <p className='home-services-txt'>{translations.homeProcessKicker}</p>
              <SplitHeading text={translations.homeProcessTitle} className='home-released-projects home-process-title' />
            </Reveal>
            <div className='home-process'>
              {processSteps.map((item, index) => (
                <Reveal key={item.step} delay={index * 0.1} className='home-process-item'>
                  <div className='home-process-photo'><img src={item.photo} alt='' loading='lazy' /></div>
                  <span className='home-process-step'>{item.step}</span>
                  <h3 className='home-process-item-title'>{item.title}</h3>
                  <p className='home-process-item-txt'>{item.text}</p>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <button className='home-process-link' onClick={() => navigate('/process')}>
                {translations.homeProcessLink} <span className='home-cta-band-arrow'>→</span>
              </button>
            </Reveal>

            <Reveal>
              <div className='home-quiz-teaser'>
                <div className='home-quiz-teaser-text'>
                  <p className='home-quiz-teaser-label'>{translations.readyKicker}</p>
                  <h2 className='home-quiz-teaser-title'>{translations.homeQuizTitle}</h2>
                  <p className='home-quiz-teaser-sub'>{translations.homeQuizSub}</p>
                </div>
                <MagneticButton className='home-quiz-teaser-btn' onClick={() => navigate('/ai-readiness')}>
                  {translations.homeQuizCta} <span className='home-cta-band-arrow'>→</span>
                </MagneticButton>
              </div>
            </Reveal>

            <Reveal>
              <div className='home-quiz-teaser home-estimate-teaser'>
                <div className='home-quiz-teaser-text'>
                  <p className='home-quiz-teaser-label'>{translations.homeEstimateLabel}</p>
                  <h2 className='home-quiz-teaser-title'>{translations.homeEstimateTitle}</h2>
                  <p className='home-quiz-teaser-sub'>{translations.homeEstimateSub}</p>
                </div>
                <MagneticButton className='home-quiz-teaser-btn' onClick={() => navigate('/estimate')}>
                  {translations.homeEstimateCta} <span className='home-cta-band-arrow'>→</span>
                </MagneticButton>
              </div>
            </Reveal>

            <div className='home-tools'>
              <p className='home-tools-label'>{translations.footerTools}</p>
              <div className='home-tools-grid'>
                {freeTools.map((tool, index) => (
                  <Reveal key={tool.to} delay={index * 0.08}>
                    <Link to={tool.to} className='home-tool-card'>
                      <span className='home-tool-kicker'>{tool.kicker}</span>
                      <span className='home-tool-title'>{tool.title}</span>
                      <span className='home-tool-arrow' aria-hidden='true'>→</span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal>
              <div className='home-industries'>
                <p className='home-industries-label'>{translations.homeIndustriesLabel}</p>
                <div className='home-industries-list'>
                  {industries.map((industry) => (
                    <span key={industry} className='home-industry-pill'>{industry}</span>
                  ))}
                </div>
              </div>
            </Reveal>

            <div className='home-engagement'>
              <EngagementModels from='home' />
            </div>

            <Reveal>
              <div className='home-cta-band'>
                <div className='home-cta-band-text'>
                  <SplitHeading text={translations.homeCtaTitle} className='home-cta-band-title' />
                  <p className='home-cta-band-sub'>{translations.homeCtaSub}</p>
                </div>
                <div className='home-cta-band-actions'>
                  <MagneticButton className='home-cta-band-btn' onClick={handleStartBrief} strength={0.5}>
                    {translations.homeCtaPrimary} <span className='home-cta-band-arrow'>→</span>
                  </MagneticButton>
                  <button className='home-cta-band-btn home-cta-band-btn-ghost' onClick={() => navigate('/contact-us')}>
                    {translations.homeCtaSecondary}
                  </button>
                </div>
              </div>
            </Reveal>

            {/* <p className='home-released-projects'>{translations.createdApps}</p> */}
            {/* <div className='slider-box'> 

            <Slider {...settings}>

              <div className='medic-minder' style={{ width: "400px" }} onClick={() => handleNavigate('/products/medicminder')}>
                <div className='medic-minder-left'>
                  <h2 className='medic-minder-left-title'>Medic Minder</h2>
                  <h2 className='medic-minder-left-head'>Medic Minder</h2>
                  <p className='medic-minder-left-txt'>{translations.home2} </p>
                  <button className='medic-minder-left-btn'>{translations.home3}</button>
                </div>
                <div>
                  <img src={Medicmind} alt='medicmind' width={650} height={350} />
                </div>
              </div>


              <div className='medic-minder' onClick={() => handleNavigate('/products/archiml')}>
                <div className='medic-minder-left'>
                  <h2 className='medic-minder-left-title'>ArchiML</h2>
                  <h2 className='medic-minder-left-head'>ArchiML</h2>
                  <p className='medic-minder-left-txt'>{translations.home1}</p>
                  <button className='medic-minder-left-btn'>{translations.home3}</button>
                </div>
                <div>
                  <img src={archiml} alt='medicmind' width={650} height={700} />
                </div>
              </div>
            </Slider>
            </div> */}


            <Reveal>
              <p className='header-head-title home-blog-sub-title'>{translations.home4}</p>
              <SplitHeading text={translations.home5} className='home-released-projects home-blog-title' />
            </Reveal>
            <div className='home-blog-container'>
              {blogBox.map((article, index) => (
                <Reveal key={article.title} delay={index * 0.12}>
                  <Link className='home-blog-img-box' to={article.link}>
                    <div className='home-blog-img-frame'>
                      <img src={article.img} className='home-blog-img' alt={article.title} />
                    </div>
                    <p className='home-blog-title-style'>{article.title}</p>
                    <p className='home-blog-desc'>{article.desc}</p>
                  </Link>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <button className='home-services-btn home-recent-work home-view-blog-btn' onClick={() => handleNavigateToArticle('/articles')}>{translations.home6}</button>
            </Reveal>
            <div className='home-faq-container'>
              <Reveal direction='right' className='home-faq-container-left'>
                <h3>{translations.uiFaq}</h3>
                <p>{translations.home7}</p>
              </Reveal>
              <Reveal direction='left' className='home-faq-reveal'>
                <FAQ />
              </Reveal>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}

export default Home